import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabase/supabase";
import WeeklyLineupManager from "../components/WeeklyLineupManager";
import LoadingScreen from "../components/LoadingScreen";
import { recordSmackTalkEvent } from '../utils/smackTalkEvent';
import { invalidateLeague } from "../utils/queryCache";
import QuickActionChips from "../components/lineup/QuickActionChips";
import { useLeague } from "../context/LeagueContext";
import { useTeams } from "../hooks/useTeams";
import { useWeeklyLineup } from "../hooks/useWeeklyLineup";
import { useSeasonConfig } from "../hooks/useSeasonConfig";
import { ChevronRight, Pencil, CheckCircle, Check, X } from "lucide-react";
import TeamDrawer from "../components/league/TeamDrawer";
import TeamLogoImage from "../components/league/TeamLogoImage";
import UserAvatar from "../components/league/UserAvatar";
import CenteredModal from "../components/sheets/CenteredModal";
import BottomSheet from "../components/sheets/BottomSheet";
import { SEASON_YEAR, isOffSeason, TOTAL_LINEUP_WEEKS, FIRST_PLAYOFF_WEEK } from "../utils/season";
import { normalizeTeamName } from "../utils/teamName";
import { getBestWeekStats, getDisplayData } from "../utils/leagueUtils";
import { containsProfanity, PROFANITY_MESSAGE } from "../utils/profanity";
import { usePageTour } from "../hooks/usePageTour";

function MyLineup() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  // Teams tab links here with ?open=waivers while bidding is open, so a
  // manager acting on that notice lands straight on their claims instead of
  // the hero card.
  const [searchParams] = useSearchParams();
  const autoOpenWaivers = searchParams.get('open') === 'waivers';
  const { currentUserId, currentMemberId, members: ctxMembers, currentWeek, loading: leagueLoading, refreshLeague, isDemo } = useLeague();
  usePageTour("lineup", { demo: isDemo, nextLabel: "Next: live scores", nextTo: `/${leagueId}/scoreboard` });
  const { teams: allTeams } = useTeams();
  const { config: seasonConfig } = useSeasonConfig();
  // One instance for the page, handed to WeeklyLineupManager below.
  //
  // Both used to call this hook with the same arguments, which is two
  // instances holding two copies of the same state. A freeze saved inside the
  // manager updated the manager's copy, and the chips here kept whatever they
  // had loaded with: the counter read "3 of 3" beside a row that was visibly
  // frozen. One owner means there is nothing to keep in step.
  const lineupState = useWeeklyLineup({
    leagueId, memberId: currentMemberId, currentWeek,
  });
  const {
    freezesRemaining, hasTripPlay, tripPlayUsedWeek, weeklyLineups,
    loading: lineupLoading,
  } = lineupState;
  const isPreseason = isOffSeason(seasonConfig?.currentWeek);

  const [seasonStartDate, setSeasonStartDate] = useState(null);
  // Local override, applied only after this manager renames their own squad.
  // LeagueContext has no refresh, so its copy of the row stays stale until
  // the next mount and the rename would otherwise appear not to have saved.
  const [teamNameOverride, setTeamNameOverride] = useState(null);
  const [smackTalkDb, setSmackTalkDb] = useState("");
  const [smackTalkDraft, setSmackTalkDraft] = useState(null);
  const [isEditingSmackTalk, setIsEditingSmackTalk] = useState(false);
  const [smackTalkSaving, setSmackTalkSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [isEditingTeamName, setIsEditingTeamName] = useState(false);
  const [editedTeamName, setEditedTeamName] = useState("");
  const [teamNameSaving, setTeamNameSaving] = useState(false);
  const [currentWeekRank, setCurrentWeekRank] = useState(null);
  const [rankChange, setRankChange] = useState(0);
  const [loadingRank, setLoadingRank] = useState(false);
  const [drawerTeam, setDrawerTeam] = useState(null);

  // The league_members row is already loaded by LeagueContext, joined with
  // users, so team name, points, avatar and manager name are read from there
  // rather than fetched again. The old second fetch also omitted first_name,
  // which is why the drawer's manager label always read "Unknown".
  const myMember = ctxMembers.find((m) => m.id === currentMemberId) || null;
  const teamName = teamNameOverride ?? (myMember?.team_name || "Unnamed Squad");
  const squadPoints = myMember?.points || 0;
  const smackTalk = smackTalkDraft ?? smackTalkDb;

  const getOrdinalSuffix = (rank) => {
    if (rank >= 11 && rank <= 13) return 'th';
    switch (rank % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const RankingLine = ({ currentRank, rankChange, loading }) => {
    if (loading) {
      return <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Loading rank…</span>;
    }
    if (!currentRank) {
      return <span style={{ fontSize: 13, color: 'var(--color-brand)', fontWeight: 600 }}>Season start</span>;
    }
    const trend = rankChange < 0 ? '↑' : rankChange > 0 ? '↓' : '';
    return (
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-brand)' }}>
        {currentRank}{getOrdinalSuffix(currentRank)} place
        {rankChange !== 0 && (
          <span style={{ marginLeft: 6, color: rankChange < 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
            {trend} {Math.abs(rankChange)}
          </span>
        )}
      </span>
    );
  };

  // Team logo used for schedule strip/sheet rows. Tapping opens the shared
  // TeamDrawer; TeamSlot renders its own captain/5x/points badges
  // separately, so this stays the plain clickable-circle primitive.
  const TeamLogo = ({ teamName, size = 48, clickable = false }) => {
    const team = allTeams?.[normalizeTeamName(teamName)];
    return (
      <button
        type="button"
        onClick={() => clickable && teamName && setDrawerTeam(teamName)}
        disabled={!clickable}
        style={{ border: 'none', background: 'none', padding: 0, cursor: clickable ? 'pointer' : 'default' }}
        title={clickable ? `View ${teamName} details` : teamName}
      >
        <TeamLogoImage teamName={teamName} primaryColor={team?.colors?.primary} size={size} />
      </button>
    );
  };

  // Current roster, resolved from this week's saved lineup (real source of
  // truth), used by both the schedule strip and the empty-slot banner.
  const currentLineup = weeklyLineups[`week${currentWeek}`] || { starters: [], bench: [] };
  const rosterSlugs = [...(currentLineup.starters || []), ...(currentLineup.bench || [])].filter(Boolean);
  const rosterTeams = rosterSlugs
    .map((slug) => allTeams?.[slug])
    .filter(Boolean);
  const emptySlotCount = 7 - rosterSlugs.length;
  const rosterSlugsKey = rosterSlugs.slice().sort().join(',');

  // Full-season matrix, fetched once per roster the first time the sheet is
  // opened. State lives here (not inside FullScheduleSheet) because that
  // component is redefined every MyLineup render, and a state hook inside a
  // component whose identity changes every render never actually persists.
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  useEffect(() => {
    const loadFullSchedule = async () => {
      if (!showFullSchedule || scheduleData !== null || rosterSlugs.length === 0) return;
      setScheduleLoading(true);
      try {
        const teamFilter = rosterSlugs.map(s => `home_team.eq.${s},away_team.eq.${s}`).join(',');
        const weekValues = Array.from({ length: TOTAL_LINEUP_WEEKS }, (_, i) => String(i + 1));
        const { data: gamesData } = await supabase
          .from('games')
          .select('week,home_team,away_team,date,game_complete,home_score,away_score,home_spread')
          .eq('year', SEASON_YEAR)
          .in('week', weekValues)
          .or(teamFilter);

        const allScheduleData = {};
        (gamesData || []).forEach(gameData => {
          const week = Number(gameData.week);
          if (!allScheduleData[week]) allScheduleData[week] = [];
          allScheduleData[week].push({
            homeTeam: gameData.home_team,
            awayTeam: gameData.away_team,
            date: gameData.date,
            gameComplete: gameData.game_complete || false,
            homeScore: gameData.home_score ?? null,
            awayScore: gameData.away_score ?? null,
            homeSpread: gameData.home_spread || null,
          });
        });
        setScheduleData(allScheduleData);
      } catch (error) {
        console.error("Error loading schedule:", error);
      } finally {
        setScheduleLoading(false);
      }
    };
    loadFullSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFullSchedule]);

  // Roster changed (e.g. a free-agent swap) since the last fetch: drop the
  // cached matrix so the next sheet open re-fetches instead of showing stale
  // opponents.
  useEffect(() => {
    setScheduleData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterSlugsKey]);

  const FullScheduleSheet = () => {
    const findTeamGame = (name, week) =>
      (scheduleData?.[week] || []).find(g => g.homeTeam === name || g.awayTeam === name);

    const getOpponentInfo = (name, week) => {
      const game = findTeamGame(name, week);
      if (!game) return null;
      const isHome = game.homeTeam === name;
      const opponentSlug = isHome ? game.awayTeam : game.homeTeam;
      const opponent = allTeams?.[opponentSlug]?.school || opponentSlug;
      const myScore = isHome ? game.homeScore : game.awayScore;
      const opponentScore = isHome ? game.awayScore : game.homeScore;
      let teamSpread = null;
      if (typeof game.homeSpread === 'number') teamSpread = isHome ? game.homeSpread : -game.homeSpread;
      return {
        opponent, isHome, gameComplete: game.gameComplete, myScore, opponentScore, teamSpread,
        won: myScore !== null && opponentScore !== null ? myScore > opponentScore : null,
      };
    };

    const weeks = Array.from({ length: TOTAL_LINEUP_WEEKS }, (_, i) => i + 1);
    const playoffLabels = { 11: 'PO 1', 12: 'PO 2', 13: 'Final' };
    const weekLabel = (week) => playoffLabels[week] || `W${week}`;

    return (
      <BottomSheet open={showFullSchedule} onClose={() => setShowFullSchedule(false)} title="Season schedule" maxHeight="90vh">
        {scheduleLoading ? (
          <div className="text-center py-10">
            <div className="w-8 h-8 border-4 border-hairline border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Loading schedule…</p>
          </div>
        ) : rosterTeams.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
            Your lineup has no teams yet. Add teams to see their schedule here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-max">
              <div className="flex gap-1 mb-2">
                <div className="w-28 text-white font-bold text-xs p-2 rounded" style={{ backgroundColor: "var(--color-brand)" }}>Team</div>
                {weeks.map(week => (
                  <div key={week} className="w-20 text-white font-bold text-xs p-2 rounded text-center" style={{ backgroundColor: week === currentWeek ? "var(--color-brand-lift)" : week >= FIRST_PLAYOFF_WEEK ? "var(--color-playoff)" : "var(--color-brand)" }}>
                    {weekLabel(week)}
                  </div>
                ))}
              </div>
              {rosterTeams.map((team) => (
                <div key={team.school} className="flex gap-1 mb-1">
                  {/* Team name, not a logo: at 28px a helmet mark is not
                      reliably identifiable, and the whole point of this row
                      is knowing whose schedule you are reading. */}
                  <div className="w-28 rounded px-2 py-1.5 flex flex-col justify-center" style={{ backgroundColor: 'var(--color-bg-page)' }}>
                    <div className="text-[11px] font-semibold leading-tight" style={{ color: 'var(--color-text-strong)' }}>{team.school}</div>
                    <div className="text-[10px] font-medium mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{team.currentSeason?.record || '0-0'}</div>
                  </div>
                  {weeks.map(week => {
                    const info = getOpponentInfo(normalizeTeamName(team.school), week);
                    let bg = 'var(--color-bg-page)';
                    if (!info) bg = 'var(--color-border)';
                    else if (info.gameComplete && info.won !== null) bg = info.won ? 'var(--color-success-bg)' : 'var(--color-danger-bg)';
                    return (
                      <div
                        key={week}
                        className="w-20 rounded p-1 text-center min-h-[54px] flex flex-col justify-center"
                        style={{ backgroundColor: bg, boxShadow: week === currentWeek ? 'inset 0 0 0 2px var(--color-brand)' : undefined }}
                      >
                        {/* Opponent names wrap rather than truncate: the
                            old two-word slice turned "Middle Tennessee State"
                            and "Miami (OH)" into the same cell text. The grid
                            already scrolls horizontally, so a wider column
                            costs nothing on a phone. */}
                        {info ? (
                          <div className="text-[10px] leading-tight">
                            {info.gameComplete && info.myScore !== null ? (
                              <>
                                <div className="font-bold" style={{ color: info.won ? 'var(--color-success-text)' : 'var(--color-danger-text)' }}>
                                  {info.myScore}-{info.opponentScore} {info.won ? 'W' : 'L'}
                                </div>
                                <div className="leading-tight" style={{ color: 'var(--color-text-muted)' }}>{info.isHome ? 'vs' : '@'} {info.opponent}</div>
                              </>
                            ) : (
                              <>
                                <div className="font-medium" style={{ color: 'var(--color-text-strong)' }}>{info.isHome ? 'vs' : '@'}</div>
                                <div className="leading-tight" style={{ color: 'var(--color-text-muted)' }}>{info.opponent}</div>
                                {info.teamSpread !== null && (
                                  <div className="font-bold" style={{ color: 'var(--color-warning)' }}>
                                    {info.teamSpread === 0 ? 'PK' : info.teamSpread > 0 ? `+${info.teamSpread}` : `${info.teamSpread}`}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="text-[10px] font-medium" style={{ color: 'var(--color-text-muted)' }}>BYE</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </BottomSheet>
    );
  };

  // Rank and smack talk are the only two things this page still has to fetch,
  // and neither depends on the other, so they go out together. They used to
  // run after a third query for the league_members row, which context already
  // had, making the page wait on three sequential round trips.
  useEffect(() => {
    if (!currentUserId || !currentMemberId) return;
    let cancelled = false;

    const load = async () => {
      setLoadingRank(true);

      const previousWeek = Math.max(1, (currentWeek || 1) - 1);
      try {
        const [{ data: standingRow }, { data: memberRow }, { data: lineupRows }] = await Promise.all([
          // maybeSingle, not single: there is no standings row until a week
          // has been scored, and single() answers "no rows" with a 406 that
          // logged as an error on every preseason load.
          supabase
            .from('weekly_standings')
            .select('rank')
            .eq('league_id', leagueId)
            .eq('member_id', currentMemberId)
            .eq('week', previousWeek.toString())
            .maybeSingle(),
          // smack_talk is the one column LeagueContext's select does not
          // carry. Anything added here must exist in the database first:
          // PostgREST rejects the whole request for one unknown column,
          // which is how this page once lost its team name and points too.
          supabase
            .from('league_members')
            .select('smack_talk')
            .eq('id', currentMemberId)
            .maybeSingle(),
          // Not filtered to a week: the standings tiebreak needs each
          // member's best week across the whole season. Without this, ties on
          // points fell back to plain points order with no tiebreak, which
          // could disagree with the standings page's rank for the same
          // manager on the same day (getDisplayData in leagueUtils.js).
          supabase
            .from('weekly_lineups')
            .select('member_id, points, captain, team_points')
            .eq('league_id', leagueId),
        ]);

        if (cancelled) return;

        // Same three-stage tiebreak the standings page uses: season points,
        // then each manager's best single week, then that week's captain
        // performance, so this badge and the standings page can never
        // disagree over a tie.
        const seasonRowsByMemberId = {};
        (lineupRows || []).forEach((row) => {
          (seasonRowsByMemberId[row.member_id] = seasonRowsByMemberId[row.member_id] || []).push(row);
        });
        const enrichedMembers = ctxMembers.map((m) => ({
          ...m,
          teamName: m.team_name,
          ...getBestWeekStats(seasonRowsByMemberId[m.id]),
        }));
        const ranked = getDisplayData({ viewMode: 'current', members: enrichedMembers });
        const rankIndex = ranked.findIndex((m) => m.user_id === currentUserId);
        const liveRank = rankIndex !== -1 ? rankIndex + 1 : null;
        setCurrentWeekRank(liveRank);

        const prevRank = standingRow?.rank || null;
        setRankChange(liveRank && prevRank ? liveRank - prevRank : 0);
        setSmackTalkDb(memberRow?.smack_talk || "");
      } catch (error) {
        console.error("Error loading lineup header data:", error);
      } finally {
        if (!cancelled) setLoadingRank(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [leagueId, currentUserId, currentMemberId, currentWeek, ctxMembers]);

  useEffect(() => {
    if (!isPreseason) return;
    const loadSeasonStart = async () => {
      const { data } = await supabase
        .from('games')
        .select('date')
        .eq('year', SEASON_YEAR)
        .gt('week', 0)
        .order('date', { ascending: true })
        .limit(1);
      if (data?.[0]?.date) setSeasonStartDate(new Date(data[0].date));
    };
    loadSeasonStart();
  }, [isPreseason]);

  const handleTeamClick = (teamName) => {
    setDrawerTeam(teamName);
  };

  const handleSaveTeamName = async () => {
    const trimmed = editedTeamName.trim();
    if (!trimmed) return;
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) return;

    setTeamNameSaving(true);
    try {
      await supabase
        .from('league_members')
        .update({ team_name: trimmed })
        .eq('league_id', leagueId)
        .eq('user_id', currentUser.id);
      setTeamNameOverride(trimmed);
      setIsEditingTeamName(false);
      // The standings list the team name off the same row.
      invalidateLeague(leagueId);
    } catch (error) {
      console.error("Error saving team name:", error);
      setSuccessMessage("Failed to save team name. Please try again.");
      setShowSuccessModal(true);
    } finally {
      setTeamNameSaving(false);
    }
  };

  const handleSaveSmackTalk = async () => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) return;

    if (containsProfanity(smackTalk)) {
      setSuccessMessage(PROFANITY_MESSAGE);
      setShowSuccessModal(true);
      return;
    }

    setSmackTalkSaving(true);
    try {
      // supabase-js resolves with { error } rather than throwing, so the catch
      // below never fired on a failed write and this reported success whatever
      // happened, including while smack_talk did not exist as a column at all
      // (migration 040). Keep the explicit error check: a silent failure here
      // is indistinguishable from a save.
      const { error } = await supabase
        .from('league_members')
        .update({ smack_talk: smackTalk.trim() })
        .eq('league_id', leagueId)
        .eq('user_id', currentUser.id);

      if (error) throw error;

      setSmackTalkDb(smackTalk.trim());
      setSmackTalkDraft(null);
      setIsEditingSmackTalk(false);

      // Announce it in the league activity feed. One row per manager per week
      // (the partial unique index from migration 042), so editing repeatedly
      // updates that row rather than burying everyone else's moves. Best
      // effort: the smack talk itself is already saved, and failing to
      // announce it is not worth reporting as a failed save.
      await recordSmackTalkEvent({
        leagueId,
        memberId: currentMemberId,
        week: Number(currentWeek) || null,
        text: smackTalk.trim(),
      });

      invalidateLeague(leagueId);
      // league_members is not in the supabase_realtime publication (see the
      // draft-room comment on this in CLAUDE.md: the publication carries no
      // tables at all), so LeagueContext's own subscription never fires for
      // this write. Recent Activity reads member.smack_talk live off that
      // same context, so without this it kept showing whatever smack talk
      // was loaded when the league page last mounted, one edit behind.
      refreshLeague?.();
    } catch (error) {
      console.error("Error saving smack talk:", error);
      setSuccessMessage("Smack talk could not be saved. Try again.");
      setShowSuccessModal(true);
    } finally {
      setSmackTalkSaving(false);
    }
  };

  if (leagueLoading || lineupLoading) {
    return <LoadingScreen message="Loading your lineup..." />;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--color-bg-page)', color: 'var(--color-text-strong)' }}>
      {/* Full bleed below sm, matching the league tab: the cards run to both
          edges on a phone and take the centred column back from sm up. */}
      <div className="mx-auto pt-4 pb-[100px] px-0 sm:px-4" style={{ maxWidth: 600 }}>

        {/* Hero card: identity, rank, points, smack talk, quick-action stats */}
        <div className="border-y sm:border sm:rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', marginBottom: 12 }}>
          <div style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <UserAvatar member={myMember || {}} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              {isEditingTeamName ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    autoFocus
                    value={editedTeamName}
                    onChange={(e) => setEditedTeamName(e.target.value.slice(0, 40))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTeamName(); if (e.key === 'Escape') setIsEditingTeamName(false); }}
                    style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)', border: '1.5px solid var(--color-brand)', borderRadius: 8, padding: '3px 8px', minWidth: 0, flex: 1 }}
                  />
                  <button
                    onClick={handleSaveTeamName}
                    disabled={teamNameSaving || !editedTeamName.trim()}
                    aria-label="Save team name"
                    style={{ border: 'none', background: 'none', padding: 4, cursor: 'pointer', color: 'var(--color-brand)', flexShrink: 0 }}
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={() => setIsEditingTeamName(false)}
                    aria-label="Cancel"
                    style={{ border: 'none', background: 'none', padding: 4, cursor: 'pointer', color: 'var(--color-text-muted)', flexShrink: 0 }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                  <h1 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{teamName}</h1>
                  {/* The pencil on its own said only that something here was
                      editable, not what. The label names it, and the name
                      beside it gives way first: a long team name truncates
                      rather than pushing the control it belongs to off the
                      row. */}
                  {!isDemo && <button
                    onClick={() => { setEditedTeamName(teamName === "Unnamed Squad" ? "" : teamName); setIsEditingTeamName(true); }}
                    aria-label="Edit team name"
                    style={{ display: 'flex', alignItems: 'center', gap: 3, border: 'none', background: 'none', padding: 2, cursor: 'pointer', color: 'var(--color-text-faint)', flexShrink: 0 }}
                  >
                    <Pencil size={12} />
                    <span style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>Edit name</span>
                  </button>}
                </div>
              )}
              <div style={{ marginTop: 1 }}>
                <RankingLine currentRank={currentWeekRank} rankChange={rankChange} loading={loadingRank} />
              </div>

              {/* Smack talk: single tap-to-edit line, not its own card */}
              <div style={{ marginTop: 6 }}>
                {isEditingSmackTalk ? (
                  <div>
                    <textarea
                      autoFocus
                      value={smackTalk}
                      onChange={(e) => setSmackTalkDraft(e.target.value.slice(0, 80))}
                      placeholder="Say something to intimidate your opponents…"
                      style={{ width: '100%', minHeight: 56, padding: 10, border: '1.5px solid var(--color-border)', borderRadius: 10, fontSize: 13, color: 'var(--color-text-strong)', resize: 'none', outline: 'none', boxSizing: 'border-box', backgroundColor: 'var(--color-bg-page)' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                      <span style={{ fontSize: 11, color: smackTalk.length > 70 ? 'var(--color-danger)' : 'var(--color-text-faint)' }}>{smackTalk.length}/80</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setIsEditingSmackTalk(false)} style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-card)', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--color-text-strong)' }}>Cancel</button>
                        <button onClick={handleSaveSmackTalk} disabled={smackTalkSaving} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', backgroundColor: 'var(--color-brand)', color: 'var(--color-bg-card)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{smackTalkSaving ? "Saving…" : "Save"}</button>
                      </div>
                    </div>
                  </div>
                ) : isDemo ? (
                  smackTalk.trim() ? (
                    <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{smackTalk}</span>
                  ) : null
                ) : (
                  <button
                    onClick={() => setIsEditingSmackTalk(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: 'none', padding: 0, cursor: 'pointer', maxWidth: '100%', textAlign: 'left' }}
                  >
                    {smackTalk.trim() ? (
                      <span style={{ fontSize: 13, color: 'var(--color-text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{smackTalk}</span>
                    ) : (
                      <span style={{ fontSize: 13, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Add smack talk</span>
                    )}
                    <Pencil size={11} color="var(--color-text-faint)" style={{ flexShrink: 0 }} />
                    {/* Only once there is something to edit. The empty state
                        already reads "Add smack talk", and a second label
                        beside it would say the same thing twice. Named in full
                        here, unlike the team name above: this line has the row
                        to itself, and the smack talk beside it truncates. */}
                    {smackTalk.trim() && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-faint)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Edit smack talk
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={() => navigate(`/${leagueId}/league`)}
              style={{ textAlign: 'right', border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0 }}
            >
              <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--color-brand)', lineHeight: 1 }}>
                {squadPoints >= 1000 ? `${(squadPoints / 1000).toFixed(1)}k` : squadPoints.toLocaleString()}
              </div>
              <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--color-text-muted)', marginTop: 2 }}>PTS</div>
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--color-bg-subtle)' }}>
            <QuickActionChips
              leagueId={leagueId}
              memberId={currentMemberId}
              freezesRemaining={freezesRemaining}
              hasTripPlay={hasTripPlay}
              tripPlayUsedWeek={tripPlayUsedWeek}
              emptySlots={emptySlotCount}
              autoOpenWaivers={autoOpenWaivers}
            />
          </div>
        </div>

        {isDemo && (
          <div
            className="mx-4 sm:mx-0"
            style={{ backgroundColor: 'var(--color-band-bg)', border: '1px solid var(--color-band-border)', color: 'var(--color-brand)', borderRadius: 14, padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 600 }}
          >
            Lineups can't be changed in the demo. Sign up to set your own each week.
          </div>
        )}

        {/* Same informational treatment as the empty-slot banner below. It
            was near-black on white, which read as an alert rather than as the
            one neutral fact on the page. */}
        {isPreseason && seasonStartDate && (
          <div
            className="text-center mx-4 sm:mx-0"
            style={{ backgroundColor: 'var(--color-band-bg)', border: '1px solid var(--color-band-border)', color: 'var(--color-brand)', borderRadius: 14, padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 600 }}
          >
            Your season begins {seasonStartDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}.
          </div>
        )}

        {rosterTeams.length > 0 && (
          <button
            onClick={() => setShowFullSchedule(true)}
            className="mx-4 sm:mx-0"
            style={{ display: 'flex', alignItems: 'center', gap: 2, border: 'none', background: 'none', padding: '0 2px', marginBottom: 12, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--color-brand)' }}
          >
            Season schedule <ChevronRight size={13} />
          </button>
        )}

        {/* Contextual banner, only when a roster slot is actually empty.
            Padded by a wrapper rather than by a margin on the button itself,
            which would have to fight its own width: 100%. */}
        {emptySlotCount > 0 && !isDemo && (
          <div className="px-4 sm:px-0">
          <button
            onClick={() => navigate(`/${leagueId}/teams`)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
              backgroundColor: 'var(--color-band-bg)', border: '1px solid var(--color-band-border)', borderRadius: 14, padding: 12, marginBottom: 16, cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--color-brand)', fontWeight: 600, flex: 1 }}>
              {emptySlotCount} empty roster slot{emptySlotCount !== 1 ? 's' : ''}. Browse free agents to fill your lineup.
            </span>
            <ChevronRight size={16} color="var(--color-brand)" />
          </button>
          </div>
        )}

        {/* Weekly lineup manager */}
        <WeeklyLineupManager
          lineupState={lineupState}
          leagueId={leagueId}
          memberId={currentMemberId}
          allTeams={allTeams}
          currentWeek={currentWeek}
          onTeamClick={handleTeamClick}
          TeamLogo={TeamLogo}
          onOpenTeam={(school) => setDrawerTeam(school)}
          userDisplayName={myMember?.first_name || "Unknown"}
          readOnly={isDemo}
        />
      </div>

      <FullScheduleSheet />

      <CenteredModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        icon={CheckCircle}
        iconTone="success"
        title="Success"
        primaryLabel="Got it"
        onPrimary={() => setShowSuccessModal(false)}
      >
        {successMessage}
      </CenteredModal>
      <TeamDrawer teamName={drawerTeam} onClose={() => setDrawerTeam(null)} onSelectTeam={setDrawerTeam} />
    </div>
  );
}

export default MyLineup;
